<template>
  <AdminLayout>
    <div class="space-y-6">
      <div class="flex items-start justify-between">
        <div>
          <h1 class="text-2xl font-heading font-bold text-mifo-primary">Produkt bearbeiten</h1>
          <p class="mt-1 text-sm text-gray-500 flex items-center">
            <span>ID: {{ form.id || route.params.id }}</span>
            <router-link
              :to="`/product/${form.id || route.params.id}/checkout`"
              target="_blank"
              class="ml-1 inline-flex items-center text-mifo-primary hover:text-mifo-accent transition-colors"
            >
              <ShoppingCart class="h-4 w-4 mr-1" />
              Zur Buchung
            </router-link>
          </p>
        </div>
        <div class="space-x-2">
          <button class="btn-outline" @click="save">Speichern</button>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div class="lg:col-span-2 space-y-6">
          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input class="input-field" v-model="form.name" />
          </div>

          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
            <div id="editor" class="border rounded-md"></div>
          </div>
        </div>

        <div class="space-y-6">
          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Preis (EUR)</label>
            <input type="number" min="0" class="input-field" v-model.number="priceEuros" />
          </div>

          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Mehrwertsteuer (%)</label>
            <input type="number" min="0" class="input-field" v-model.number="form.vatRate" />
          </div>

          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Versandkosten (EUR)</label>
            <input type="number" min="0" step="0.01" class="input-field" v-model.number="sendingCostsEuros" placeholder="0.00" />
            <p class="text-xs text-gray-500 mt-1">Leer lassen wenn keine Versandkosten anfallen</p>
          </div>

          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-2">Produkttyp</label>
            <div class="space-y-2">
              <label class="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" name="productType" :value="false" v-model="form.allowMultiplePerOrder" />
                Einzelauswahl (1 Stück)
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" name="productType" :value="true" v-model="form.allowMultiplePerOrder" />
                Mehrfachauswahl (Anzahl wählbar)
              </label>
            </div>
          </div>

          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Lagerbestand</label>
            <input type="number" class="input-field" v-model.number="form.stockQuantity" placeholder="Leer für unbegrenzt" />
            <p class="text-xs text-gray-500 mt-2">Leer lassen für unbegrenzte Menge</p>
          </div>

          <div class="card">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700">Aktiv</span>
              <input type="checkbox" v-model="form.isActive" />
            </div>
          </div>

          <div class="card">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700">Als Add-on verfügbar</span>
              <input type="checkbox" v-model="form.isAddonEnabled" />
            </div>
          </div>
          <div class="card">
            <label class="block text-sm font-medium text-gray-700 mb-1">Sortierreihenfolge</label>
            <input type="number" min="0" class="input-field" v-model.number="form.sortOrder" placeholder="0" />
            <p class="text-xs text-gray-500 mt-1">Niedrigere Zahlen werden zuerst angezeigt</p>
          </div>
        </div>
      </div>

      <OrdersTable
        title="Bestellungen"
        :orders="orders"
        :show-price-category="false"
        @row-click="order => $router.push(`/herzbeben/products/${form.id || route.params.id}/order/${order.id}`)"
      />
    </div>
  </AdminLayout>
</template>

<script>
import { onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/utils/axios'
import AdminLayout from '@/components/Layout/AdminLayout.vue'
import OrdersTable from '@/components/Admin/OrdersTable.vue'
import { ShoppingCart } from 'lucide-vue-next'

export default {
  name: 'ProductDetail',
  components: { AdminLayout, OrdersTable, ShoppingCart },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const form = reactive({
      id: '', name: '', priceCents: 0, vatRate: 19, isActive: false, isAddonEnabled: false, allowMultiplePerOrder: true, stockQuantity: null, descriptionHtml: '', sendingCostsCents: null, sortOrder: 0
    })
    const priceEuros = ref(0)
    const sendingCostsEuros = ref(0)
    let quill
    const orders = ref([])
    const ordersLoading = ref(false)

    const load = async () => {
      const { data } = await api.get(`/products/admin/${route.params.id}`)
      Object.assign(form, data)
      priceEuros.value = (form.priceCents || 0) / 100
      sendingCostsEuros.value = (form.sendingCostsCents || 0) / 100
      if (quill && form.descriptionHtml) {
        quill.root.innerHTML = form.descriptionHtml
      }
      try {
        ordersLoading.value = true
        const res = await api.get(`/products/admin/${route.params.id}/orders`)
        orders.value = res.data || []
      } finally {
        ordersLoading.value = false
      }
    }

    const save = async () => {
      try {
        form.priceCents = Math.round((priceEuros.value || 0) * 100)
        form.sendingCostsCents = sendingCostsEuros.value ? Math.round((sendingCostsEuros.value || 0) * 100) : null
        form.descriptionHtml = quill ? quill.root.innerHTML : form.descriptionHtml
        const { data } = await api.put(`/products/${route.params.id}`, form)
        Object.assign(form, data)
        router.push({ name: 'ProductList' })
      } catch (e) {
        console.error('Fehler beim Speichern des Produkts:', e)
        alert(e?.response?.data?.error || 'Speichern fehlgeschlagen')
      }
    }

    onMounted(async () => {
      // Lazy import Quill to avoid bundling issues if not used elsewhere
      const Quill = (await import('quill')).default
      await import('quill/dist/quill.snow.css')
      quill = new Quill('#editor', { theme: 'snow' })
      await load()
    })

    return { form, priceEuros, sendingCostsEuros, save, orders, ordersLoading, route }
  }
}
</script>

